import { useDispatch, useSelector } from 'react-redux';
import { actionSetQueryResult } from '../data/action';
import { useUiDashboardQuery, useUiDashboardQueryIsLoading } from './useUi';
import { queryOql } from '../data/repository';
import { useFormatDate, useParseDate } from '@octavius-rocks/ui';
import { useEffect, useState } from 'react';
import { useDashboardQueryArgs } from './useQueryArgs';
import { mapValues } from '../utils/oql.js';


/**
 * get data
 */
export const useQueryResult = (key) => {
	return useSelector(({query})=> query[key] || {})
}

/**
 * equivalent to useState
 * @param {string} key store key
 */
export const useQueryState = (key)=>{
	const dispatch = useDispatch()
	const result = useQueryResult(key)
	return [
		result.value || [],
		(oql) => {
			return queryOql(oql).then(result =>{
				dispatch(actionSetQueryResult(key, {
					oql:oql,
					value: mapValues(result.value),
					error: result.error
				}))
			})
		},
		result,
	]
}

/**
 * use top contents store
 * @param {string} key store key
 * @param {object} props for oql
 * @param {string} props.event_type
 * @param {Date} props.from
 * @param {Date} props.to
 * @param {integer} props.numberOfItems
 */
export const useQueryStatePageviews = (key, props = {})=>{
	const [value, fetch] = useQueryState('pageviews_'+key)
	const formatDate = useFormatDate();
	useEffect(()=>{
		const {
			event_type = "pageview",
			from,
			to,
			aggregation = "day",
		} = props;
		fetch({
			fields:[
				{
					field: "hits",
					operations: ["sum"],
					as: "hits"
				},
				"timestamp",
			],
			conditions:{
				entries:[
					{
						"field":"event_type",
						"value": event_type,
						"compare":"="
					},
					{
						"field":"timestamp",
						"value": formatDate(from, "yyyy-MM-dd HH:mm:ss"),
						"compare":">=",
					},
					{
						"field":"timestamp",
						"value": formatDate(to, "yyyy-MM-dd HH:mm:ss"),
						"compare":"<=",
					},
				],
				relation: "AND"
			},
			aggregation: aggregation,
			order_by:[ {field:"timestamp", direction: "ASC"}]
		});
	}, [key, JSON.stringify(props)])

	return value
}


/**
 * use top contents store
 * @param {string} key store key
 * @param {object} props for oql
 * @param {string} props.event_type
 * @param {Date} props.from
 * @param {Date} props.to
 * @param {integer} props.numberOfItems
 */
export const useQueryStateTopContentIds = (key, props = {})=>{
	const [value, fetch] = useQueryState('topContents_'+key)
	const formatDate = useFormatDate()
	useEffect(()=>{
		const {
			event_type = "pageview",
			from = new Date(Date.now() - 1000* 60 * 60 * 24 * 7),
			to = new Date(),
			numberOfItems = 10,
		} = props;
		fetch({
			fields:[
				{
					field: "hits",
					operations: ["sum"],
					as: "hits"
				},
				"content_id",
				"content_type",
			],
			conditions:{
				entries:[
					{
						"field":"event_type",
						"value": event_type,
						"compare":"="
					},
					{
						"field":"timestamp",
						"value": formatDate(from, "yyyy-MM-dd HH:mm:ss"),
						"compare":">=",
					},
					{
						"field":"timestamp",
						"value": formatDate(to, "yyyy-MM-dd HH:mm:ss"),
						"compare":"<=",
					},
				],
				relation: "AND"
			},
			order_by:[ {field:"hits", direction: "DESC"}],
			group_by: [ "content_id"],
			limit: numberOfItems,
			page: 1,
		})
	}, [key, JSON.stringify(props)])

	return value
}

/**
 * use top contents store
 * @param {string} key store key
 * @param {object} props for oql
 * @param {string} props.event_type
 * @param {Date} props.from
 * @param {Date} props.to
 * @param {integer} props.numberOfItems
 */
export const useQueryStateTopReferer = (key, props = {})=>{
	const [value, fetch] = useQueryState('topReferer_'+key)
	const formatDate = useFormatDate()
	useEffect(()=>{
		const {
			event_type = "pageview",
			from = new Date(Date.now() - 1000* 60 * 60 * 24 * 7),
			to = new Date(),
			numberOfItems = 10,
		} = props
		fetch({
			fields:[
				{
					field: "hits",
					operations: ["sum"],
					as: "hits"
				},
				"referer_domain",
			],
			conditions:{
				entries:[
					{
						"field":"event_type",
						"value": event_type,
						"compare":"="
					},
					{
						"field":"timestamp",
						"value": formatDate(from, "yyyy-MM-dd HH:mm:ss"),
						"compare":">=",
					},
					{
						"field":"timestamp",
						"value": formatDate(to, "yyyy-MM-dd HH:mm:ss"),
						"compare":"<=",
					},
				],
				relation: "AND"
			},
			order_by:[ {field:"hits", direction: "DESC"}],
			group_by: [ "referer_domain"],
			limit: numberOfItems,
			page: 1,
		})
	}, [key, JSON.stringify(props)])

	return value
}



// --------------------------------------------------
// hard coded stores
// --------------------------------------------------

/**
 * list of available stores
 */
export const useQueryResultPageviews = ()=> useQueryResult('pageviews')
export const useQueryResultTopContentIds = ()=> useQueryResult('topContentIds')
export const useQueryResultTopReferer = ()=> useQueryResult('topReferer')
export const useQueryResultScreenSizes = ()=> useQueryResult('screenSizes')

/**
 * collect conditions of query ui
 */
export const useCollectQueryConditions = ()=>{
	const [args] = useUiDashboardQuery();
	const formatDate = useFormatDate();

	return ()=>{
		let entries = [];
		try{
			entries = [
				{
					"field":"timestamp",
					"value": formatDate(args.from, "yyyy-MM-dd HH:mm:ss"),
					"compare":">=",
				},
				{
					"field":"timestamp",
					"value": formatDate(args.to, "yyyy-MM-dd HH:mm:ss"),
					"compare":"<=",
				},
			];
		} catch(e){
			console.error(e);
			entries = [
				{
					"field":"timestamp",
					"value": formatDate(args.from, "yyyy-MM-dd HH:mm:ss"),
					"compare":">=",
				},
				{
					"field":"timestamp",
					"value": formatDate(args.to, "yyyy-MM-dd HH:mm:ss"),
					"compare":"<=",
				}
			];
		}
		if(args.conditions){
			entries.push(args.conditions);
		}

		return {
			entries,
			relation: "AND",
		}
	}
}

/**
 * check for aggregation
 */
export const useCollectQueryAggregation = ()=>{
	const [args] = useUiDashboardQuery()
	return ()=> args.aggregation
}

/**
 * fetch data
 */
export const useSubmitDashboardQuery = ()=>{
	const dispatch = useDispatch()
	const [ args ] = useUiDashboardQuery() // args for submission
	const [_, setQueryArgs] = useDashboardQueryArgs() // save to config that is persistet
	const [isLoading, setIsLoading] = useUiDashboardQueryIsLoading()

	const parseDate = useParseDate();
	const getConditions = useCollectQueryConditions();
	const getAggregation = useCollectQueryAggregation();
	const submit = ()=>{

		setIsLoading(true)

		setQueryArgs(args)

		const conditions = getConditions()

		// pageview per time
		const pageviewsOql = {
			fields:[
				{
					field: "hits",
					operations: ["sum"],
					as: "hits"
				},
				"timestamp",
			],
			conditions,
			aggregation: getAggregation(),
			order_by:[ {field:"timestamp", direction: "ASC"}]
		}
		queryOql(pageviewsOql).then(result =>{
			setIsLoading(false)

			// fill empty days
			const from = args.from
			const to = args.to



			dispatch(actionSetQueryResult("pageviews", {
				oql:pageviewsOql, 
				value: mapValues(result.value)
			}))
		})

		// top content ids
		const topContentIdsOql = {
			fields:[
				{
					field: "hits",
					operations: ["sum"],
					as: "hits"
				},
				"content_id",
				"content_type",
			],
			conditions,
			order_by:[ {field:"hits", direction: "DESC"}],
			group_by: [ "content_id"],
			limit: 10,
			page: 1,
		}
		queryOql(topContentIdsOql).then(result=>{
			if(!result.error){
				dispatch(actionSetQueryResult("topContentIds", {
					value: mapValues(result.value), 
					oql: topContentIdsOql
				}))
			}
		})
		// TODO: fetch missing post ids

		// pageview for referer
		const topRefererOql = {
			fields:[
				{
					field: "hits",
					operations: ["sum"],
					as: "hits"
				},
				"referer_domain",
			],
			conditions,
			order_by:[ {field:"hits", direction: "DESC"}],
			group_by: [ "referer_domain"],
			limit: 10,
			page: 1,
		}
		queryOql(topRefererOql).then(result=>{
			if(!result.error){
				dispatch(actionSetQueryResult("topReferer", {
					value: mapValues(result.value), 
					oql: topRefererOql
				}))
			}
		})

		// screen sizes
		const screenSizesOql = {
			fields:[
				{
					field: "hits",
					operations: ["sum"],
					as: "hits"
				},
				"breakpoint",
			],
			conditions,
			order_by:[ {field:"hits", direction: "DESC"}],
			group_by: [ "breakpoint"],
		}
		queryOql(screenSizesOql).then(result=>{
			if(!result.error){
				dispatch(actionSetQueryResult("screenSizes", {
					value: mapValues(result.value),
					oql: screenSizesOql
				}))
			}
		})
	}

	return [
		isLoading,
		submit,
	]
}